import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../lib/api-client';

interface User {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  emailVerified?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  initiateLogin: (email: string, countryCode: string) => Promise<{
    hasSharedKyc: boolean;
    kycStatus: string;
    kycRequirements: any;
  }>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if session exists (cookie-based)
        const response = await api.auth.me();
        setUser({
          userId: response.data.userId,
          email: response.data.email
        });
      } catch (error: any) {
        // Not authenticated or session expired
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const initiateLogin = async (email: string, countryCode: string) => {
    const response = await api.auth.initiate(email, countryCode);
    // Return KYC status info so UI can show appropriate screen
    return {
      hasSharedKyc: response.data.hasSharedKyc,
      kycStatus: response.data.kycStatus,
      kycRequirements: response.data.kycRequirements
    };
  };

  const verifyOtp = async (email: string, otp: string) => {
    const response = await api.auth.verify(email, otp);

    // Session created via httpOnly cookie - no token storage needed
    // Fetch user info to update state
    const meResponse = await api.auth.me();
    setUser({
      userId: meResponse.data.userId,
      email: meResponse.data.email
    });
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      setUser(null);
    }
  };

  const logoutAll = async () => {
    try {
      // Logout from all devices
      const response = await fetch('/api/auth/logout-all', {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to logout from all devices');
      }
    } catch (error) {
      console.error('Logout all error:', error);
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        initiateLogin,
        verifyOtp,
        logout,
        logoutAll,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
